import { FeatureFlagsService } from '@anecdotes/feature-flags';
import { UserFacadeService } from '@anecdotes/identity';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { switchMap, take, tap } from 'rxjs/operators';
import {
  ControlsFacadeService,
  CustomerFacadeService,
  EmployeesSotFacadeService,
  EvidenceFacadeService,
  FrameworksFacadeService,
  FrameworksFoldersFacadeService,
  PluginFacadeService,
  PoliciesFacadeService,
  RequirementsFacadeService,
} from '@anecdotes/data-store';
import { AppInitializedAction, InitializationActionType } from '../actions';

@Injectable()
export class InitializationEffects {

  constructor(
    private actions$: Actions,
    private store: Store,
    private usersFacade: UserFacadeService,
    private frameworkFacade: FrameworksFacadeService,
    private controlsFacade: ControlsFacadeService,
    private requirementsFacade: RequirementsFacadeService,
    private policyFacade: PoliciesFacadeService,
    private evidencesFacade: EvidenceFacadeService,
    private pluginFacade: PluginFacadeService,
    private customerFacade: CustomerFacadeService,
    private employeeSotFacadeService: EmployeesSotFacadeService,
    private featureFlagsService: FeatureFlagsService,
    private frameworksFoldersFacade: FrameworksFoldersFacadeService,
  ) {}

  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InitializationActionType.InitApp),
        take(1),
        switchMap(async () => this.initDataAsync()),
        tap(() => this.store.dispatch(new AppInitializedAction()))
      ),
    { dispatch: false }
  );

  private async initDataAsync(): Promise<Action[] | void> {
    const tasks = [
      this.initFrameworksAsync(),
      this.initFrameworksFoldersAsync(),
      this.initControlsAsync(),
      this.initRequirementsAsync(),
      this.initEvidenceAsync(),
      this.initPluginsAsync(),
      this.initEmployeeListAsync(),
      this.initPoliciesAsync(),
      this.initCustomerAsync(),
      this.initUsersAsync(),
    ].map((task: Promise<Action | void>) => task.then((action: Action) => {
      if (action?.type) {
        this.store.dispatch(action);
      }
    })).map((task) => task.catch(() => null));

    await Promise.all(tasks);
  }

  private initEmployeeListAsync(): Promise<void> {
    // TODO: BE does not return this permissions. As a workaround Admin role used here
    // TOOD: This mustn't be initialized on init for entire platform. It must be loaded when user lands on Employees list page.
    return this.loadIfPermittedAsync(['employeeslist:list'], () => this.employeeSotFacadeService.initAsync());
  }

  private initFrameworksAsync(): Promise<void> {
    return this.loadIfPermittedAsync([], () => this.frameworkFacade.initAsync());
  }

  private initFrameworksFoldersAsync(): Promise<void> {
    return this.loadIfPermittedAsync([], () => this.frameworksFoldersFacade.initFrameworksFoldersAsync());
  }

  private initControlsAsync(): Promise<void> {
    return this.loadIfPermittedAsync([], () => this.controlsFacade.initAsync());
  }

  private initRequirementsAsync(): Promise<void> {
    return this.loadIfPermittedAsync([], () => this.requirementsFacade.initAsync());
  }

  private initPoliciesAsync(): Promise<void> {
    // TODO: RoleEnum.Stakeholder is used here until we change API behavior
    // so that it returns empty array instead of 403 when user does not have access to resources
    return this.loadIfPermittedAsync([], () => this.policyFacade.initAsync());
  }

  private initEvidenceAsync(): Promise<void> {
    return this.loadIfPermittedAsync([], () => this.evidencesFacade.initAsync());
  }

  private initPluginsAsync(): Promise<void> {
    return this.loadIfPermittedAsync([], () => this.pluginFacade.initAsync());
  }

  private async initCustomerAsync(): Promise<void> {
    return this.loadIfPermittedAsync(['customer:read'], () => this.customerFacade.initAsync());
  }

  private initUsersAsync(): Promise<void> {
    return this.loadIfPermittedAsync([], () => this.usersFacade.initAsync());
  }

  private async loadIfPermittedAsync(requiredPermissions: string[], callback: () => Promise<void>): Promise<void> {
    if (!requiredPermissions?.length) {
      return callback();
    }

    if (await this.featureFlagsService.hasPermissionAsync(requiredPermissions)) {
      await callback();
    }
  }
}
